import React from 'react';

export type FlashcardRating = 'again' | 'hard' | 'good' | 'easy';

/** Tap-to-flip card face shared by the flashcards page and the public diagnostic. */
export const FlashcardFace: React.FC<{
  front?: string;
  back?: string;
  flipped: boolean;
  onFlip: () => void;
}> = ({ front, back, flipped, onFlip }) => (
  <button
    onClick={onFlip}
    className="card w-full min-h-[16rem] flex flex-col justify-center items-center text-center cursor-pointer hover:-translate-y-0.5 transition-transform"
  >
    {front === undefined ? (
      <p className="body-base text-neutral-600">Loading your cards…</p>
    ) : flipped ? (
      <div className="space-y-3">
        <span className="eyebrow">Answer</span>
        <p className="body-large text-neutral-800 whitespace-pre-line">{back}</p>
      </div>
    ) : (
      <div className="space-y-3">
        <span className="eyebrow">Question</span>
        <p className="body-large text-neutral-800">{front}</p>
        <p className="caption mt-4">Tap to reveal the answer</p>
      </div>
    )}
  </button>
);

const RATINGS: { rating: FlashcardRating; label: string; sub: string; bg: string; hover: string; dark?: boolean }[] = [
  { rating: 'again', label: 'Again', sub: 'Missed it', bg: 'bg-error-500', hover: 'hover:bg-error-600' },
  { rating: 'hard', label: 'Hard', sub: 'Struggled', bg: 'bg-warning-500', hover: 'hover:bg-warning-600' },
  { rating: 'good', label: 'Good', sub: 'Got it', bg: 'bg-celebrate-500', hover: 'hover:bg-celebrate-600', dark: true },
  { rating: 'easy', label: 'Easy', sub: 'Confident', bg: 'bg-success-500', hover: 'hover:bg-success-600' },
];

export const FlashcardRateButtons: React.FC<{ busy?: boolean; onRate: (rating: FlashcardRating) => void }> = ({
  busy = false,
  onRate,
}) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    {RATINGS.map(({ rating, label, sub, bg, hover, dark }) => (
      <button
        key={rating}
        type="button"
        onClick={() => onRate(rating)}
        disabled={busy}
        data-testid={`flashcard-rate-${rating}`}
        className={`${bg} ${hover} disabled:opacity-50 rounded-xl px-4 py-3 font-display font-semibold transition-colors ${
          dark ? 'text-[rgb(var(--color-on-celebrate))]' : 'text-white'
        }`}
      >
        {label}
        <span className={`block text-xs font-sans font-normal ${dark ? 'opacity-70' : 'opacity-80'}`}>{sub}</span>
      </button>
    ))}
  </div>
);
